import { useCallback, useEffect, useRef, useState } from 'react'

export type Resource<T> = {
  data: T | null
  error: unknown
  /** True only on the first load, so a refresh never flashes the skeleton back. */
  loading: boolean
  refreshing: boolean
  reload: () => void
}

/**
 * The four states every data view in web-routes-and-states.md has to render: loading, error,
 * empty (a `null`/empty `data`), and success. Deliberately small — a cache layer can replace it
 * later without touching the screens.
 */
export function useResource<T>(load: () => Promise<T>, deps: readonly unknown[]): Resource<T> {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [nonce, setNonce] = useState(0)

  // The loader closes over the caller's deps; this hook re-runs when those change.
  const run = useCallback(load, deps)
  const previousRun = useRef(run)

  useEffect(() => {
    let cancelled = false

    // A new loader means a different subject — another project, say. Showing the last one's data
    // under the new heading would be worse than showing a skeleton, so we start from nothing.
    const isNewSubject = previousRun.current !== run
    previousRun.current = run
    if (isNewSubject) {
      setData(null)
      setError(null)
      setLoading(true)
    }
    setRefreshing(true)

    run()
      .then((value) => {
        if (cancelled) return
        setData(value)
        setError(null)
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause)
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
        setRefreshing(false)
      })

    return () => {
      cancelled = true
    }
  }, [run, nonce])

  return {
    data,
    error,
    loading,
    refreshing,
    reload: useCallback(() => setNonce((value) => value + 1), []),
  }
}
